/**
 * Utilitários para manipular campos de DATA pura (sem hora) evitando
 * deslocamento de fuso horário.
 *
 * Problema: colunas Postgres do tipo DATE retornam "YYYY-MM-DD" e o
 * `new Date("YYYY-MM-DD")` é interpretado como UTC midnight, fazendo a
 * data exibida em fusos negativos (ex.: São Paulo) cair um dia antes.
 *
 * @version 1.0.0
 * RELEASE: 2026-05-27 - Versão inicial.
 */

/**
 * Formata um valor de data (DATE ou ISO completo) como "dd/MM/yyyy" sem
 * sofrer shift de timezone para strings "YYYY-MM-DD".
 */
export function formatDateOnlyBR(value: string | null | undefined): string {
  if (!value) return '-';

  // Caso DATE puro: "YYYY-MM-DD" (pode vir com ou sem hora)
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }

  // Fallback: tentar parse normal
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/**
 * Converte um `Date` (em componentes locais) para uma string ISO ancorada
 * em meio-dia UTC. Isso garante que a data armazenada em uma coluna DATE
 * seja exatamente a selecionada pelo usuário, independente do fuso.
 */
export function toDateOnlyISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T12:00:00.000Z`;
}

/**
 * Faz parse de um valor DATE/ISO como Date local (meia-noite local),
 * evitando o shift UTC que ocorre com `new Date("YYYY-MM-DD")`.
 * Retorna `null` para valores vazios/inválidos.
 */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;

  // "YYYY-MM-DD" (com ou sem parte de hora) -> constrói em horário local
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // Fallback: ISO completo com timezone definido
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

